import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { vouchersApi } from '../../utils/vouchersApi';
import { usePermission } from '../../hooks/usePermission';
import { useLanguage } from '../../contexts/LanguageContext';
import { displayName as dn } from '../../utils/displayName';
import './VoucherFormPage.css';

const TYPE_LABEL = { JV: 'Journal Voucher', RV: 'Receipt Voucher', PV: 'Payment Voucher' };

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function toNum(v) {
  if (v === '' || v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmt(n) {
  return Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const emptyLine = (defaults = {}) => ({
  accountId: '',
  accountCode: '',
  accountName: '',
  accountArabicName: '',
  currencyId: defaults.currencyId || '',
  exchangeRate: 1,
  debit: 0,
  credit: 0,
  description: '',
});

const VoucherFormPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !id;

  const canCreate  = usePermission('vouchers.create');
  const canUpdate  = usePermission('vouchers.update');
  const canPost    = usePermission('vouchers.post');
  const canReverse = usePermission('vouchers.reverse');
  const { lang } = useLanguage();

  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [voucher, setVoucher] = useState(null);

  // Form state
  const [type, setType] = useState(searchParams.get('type') || 'JV');
  const [date, setDate] = useState(todayISO());
  const [docNumber, setDocNumber] = useState('');
  const [description, setDescription] = useState('');
  const [entityId, setEntityId] = useState('');
  const [lines, setLines] = useState([]);

  // Reference data
  const [currencies, setCurrencies] = useState([]);
  const [entities, setEntities] = useState([]);
  const baseCurrency = useMemo(() => currencies.find((c) => c.isBase), [currencies]);

  // Account search popovers (per line)
  const [accountQueryByLine, setAccountQueryByLine] = useState({});
  const [accountResultsByLine, setAccountResultsByLine] = useState({});

  // ── Load reference + voucher ───────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [curr, ent] = await Promise.all([
          api.get('/currencies'),
          api.get('/entities'),
        ]);
        if (cancelled) return;
        setCurrencies(curr || []);
        setEntities((ent || []).filter((e) => e.isActive));
        const baseId = (curr || []).find((c) => c.isBase)?.id || (curr || [])[0]?.id || '';

        if (isNew) {
          setLines([emptyLine({ currencyId: baseId }), emptyLine({ currencyId: baseId })]);
          setLoading(false);
        } else {
          const v = await vouchersApi.get(id);
          if (cancelled) return;
          setVoucher(v);
          setType(v.type);
          setDate(v.date ? v.date.slice(0, 10) : todayISO());
          setDocNumber(v.docNumber || '');
          setDescription(v.description || '');
          setEntityId(v.entityId || '');
          setLines(
            (v.lines || []).map((l) => ({
              accountId: l.accountId,
              accountCode: l.account?.code || '',
              accountName: l.account?.name || '',
              accountArabicName: l.account?.arabicName || '',
              currencyId: l.currencyId,
              exchangeRate: Number(l.exchangeRate || 1),
              debit:  Number(l.debit  || 0),
              credit: Number(l.credit || 0),
              description: l.description || '',
            })),
          );
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load voucher');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew]);

  const isDraft    = !voucher || voucher.status === 'DRAFT';
  const isPosted   = voucher?.status === 'POSTED';
  const isReversed = voucher?.status === 'REVERSED';
  const isEditable = isNew || isDraft;
  const readOnly   = !isEditable;

  // ── Totals ─────────────────────────────────────────────────────────────
  const { totalDebit, totalCredit, difference, balanced } = useMemo(() => {
    let td = 0, tc = 0;
    for (const l of lines) {
      const rate = toNum(l.exchangeRate) || 1;
      td += toNum(l.debit)  * rate;
      tc += toNum(l.credit) * rate;
    }
    const diff = Math.round((td - tc) * 100) / 100;
    return {
      totalDebit:  Math.round(td * 100) / 100,
      totalCredit: Math.round(tc * 100) / 100,
      difference:  diff,
      balanced:    Math.abs(diff) < 0.01 && td > 0,
    };
  }, [lines]);

  // ── Line ops ───────────────────────────────────────────────────────────
  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const addLine = () => {
    setLines((prev) => [...prev, emptyLine({ currencyId: baseCurrency?.id })]);
  };

  const removeLine = (idx) => {
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  };

  // Account search — debounced via simple delay
  const onAccountSearch = useCallback(async (idx, q) => {
    setAccountQueryByLine((p) => ({ ...p, [idx]: q }));
    if (!q || q.length < 2) {
      setAccountResultsByLine((p) => ({ ...p, [idx]: [] }));
      return;
    }
    try {
      const data = await api.get(`/accounts?search=${encodeURIComponent(q)}&type=POSTING&limit=10`);
      setAccountResultsByLine((p) => ({ ...p, [idx]: data.items || [] }));
    } catch {
      setAccountResultsByLine((p) => ({ ...p, [idx]: [] }));
    }
  }, []);

  const selectAccount = (idx, account) => {
    updateLine(idx, {
      accountId: account.id,
      accountCode: account.code,
      accountName: account.name,
      accountArabicName: account.arabicName || '',
      currencyId: account.currencyId || baseCurrency?.id || '',
    });
    setAccountQueryByLine((p) => ({ ...p, [idx]: '' }));
    setAccountResultsByLine((p) => ({ ...p, [idx]: [] }));
  };

  // ── Persist ────────────────────────────────────────────────────────────
  const buildPayload = () => ({
    type,
    date,
    docNumber:   docNumber || null,
    description: description || null,
    entityId:    entityId || null,
    lines: lines.map((l) => ({
      accountId:    l.accountId,
      currencyId:   l.currencyId,
      exchangeRate: toNum(l.exchangeRate) || 1,
      debit:        toNum(l.debit),
      credit:       toNum(l.credit),
      description:  l.description || null,
    })),
  });

  const validate = () => {
    if (!date) return 'Date is required';
    if (lines.length === 0) return 'At least one line is required';
    for (const [i, l] of lines.entries()) {
      if (!l.accountId)  return `Line ${i + 1}: account is required`;
      if (!l.currencyId) return `Line ${i + 1}: currency is required`;
      const dr = toNum(l.debit), cr = toNum(l.credit);
      if (dr <= 0 && cr <= 0) return `Line ${i + 1}: enter debit OR credit > 0`;
      if (dr > 0 && cr > 0)   return `Line ${i + 1}: cannot have both debit and credit`;
    }
    return null;
  };

  const onSaveDraft = async () => {
    setError(null); setInfo(null);
    const err = validate();
    if (err) { setError(err); return; }
    setBusy(true);
    try {
      if (isNew) {
        const v = await vouchersApi.create(buildPayload());
        navigate(`/vouchers/${v.id}`, { replace: true });
      } else {
        const v = await vouchersApi.update(id, buildPayload());
        setVoucher(v);
        setInfo('Draft saved.');
      }
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const onPost = async () => {
    setError(null); setInfo(null);
    if (!balanced) { setError('Cannot post — debit and credit totals must match'); return; }
    if (!window.confirm('Posting is FINAL. The voucher cannot be edited after this. Continue?')) return;
    setBusy(true);
    try {
      // If new, save first
      if (isNew) {
        const v = await vouchersApi.create(buildPayload());
        const posted = await vouchersApi.post(v.id);
        navigate(`/vouchers/${posted.id}`, { replace: true });
      } else {
        // Save any pending edits first if it's draft and dirty (we don't track dirty — always save)
        if (isDraft) await vouchersApi.update(id, buildPayload());
        const posted = await vouchersApi.post(id);
        setVoucher(posted);
        setInfo('Voucher posted.');
      }
    } catch (err) {
      setError(err.message || 'Post failed');
    } finally {
      setBusy(false);
    }
  };

  const onReverse = async () => {
    if (!window.confirm('Generate a reversal voucher? This will create a new POSTED voucher with mirrored debits/credits.')) return;
    setError(null); setInfo(null);
    setBusy(true);
    try {
      const reversal = await vouchersApi.reverse(id, {});
      navigate(`/vouchers/${reversal.id}`);
    } catch (err) {
      setError(err.message || 'Reverse failed');
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Delete this DRAFT voucher? This cannot be undone.')) return;
    setError(null);
    setBusy(true);
    try {
      await vouchersApi.remove(id);
      navigate('/vouchers');
    } catch (err) {
      setError(err.message || 'Delete failed');
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="voucher-form-page">
        <div className="voucher-loading"><i className="fas fa-spinner fa-spin"></i> Loading…</div>
      </div>
    );
  }

  return (
    <div className="voucher-form-page">
      <div className="voucher-form-header">
        <div className="voucher-form-header-left">
          <h1 className="voucher-form-title">
            {isNew ? `New ${TYPE_LABEL[type]}` : `${TYPE_LABEL[voucher.type]} #${voucher.voucherNumber}`}
            <span className={`type-pill type-${type.toLowerCase()}`}>{type}</span>
            {voucher && (
              <span className={`status-pill status-${voucher.status.toLowerCase()}`}>
                {voucher.status}
              </span>
            )}
          </h1>
          {voucher && (
            <p className="voucher-form-sub">
              {voucher.fiscalYear?.code} · {voucher.period?.code}
              {voucher.postedBy && (
                <> · Posted by {voucher.postedBy.fullName || voucher.postedBy.username}</>
              )}
              {voucher.reverses && (
                <> · Reverses <a href={`/vouchers/${voucher.reverses.id}`}>{voucher.reverses.type}-{voucher.reverses.voucherNumber}</a></>
              )}
              {voucher.reversedBy && (
                <> · Reversed by <a href={`/vouchers/${voucher.reversedBy.id}`}>{voucher.reversedBy.type}-{voucher.reversedBy.voucherNumber}</a></>
              )}
            </p>
          )}
        </div>
        <div className="voucher-form-actions">
          <button type="button" className="btn-back" onClick={() => navigate('/vouchers')}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
          {isDraft && canUpdate && (
            <button type="button" className="btn-save" disabled={busy} onClick={onSaveDraft}>
              <i className="fas fa-save"></i> Save Draft
            </button>
          )}
          {isDraft && !isNew && canUpdate && (
            <button type="button" className="btn-delete" disabled={busy} onClick={onDelete}>
              <i className="fas fa-trash"></i> Delete
            </button>
          )}
          {isDraft && canPost && (
            <button
              type="button"
              className="btn-post"
              disabled={busy || !balanced}
              onClick={onPost}
              title={!balanced ? 'Debit and credit totals must match' : 'Post (final)'}
            >
              <i className="fas fa-check-double"></i> Post
            </button>
          )}
          {isPosted && canReverse && (
            <button type="button" className="btn-reverse" disabled={busy} onClick={onReverse}>
              <i className="fas fa-undo"></i> Reverse
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="voucher-alert voucher-alert-error">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}
      {info && (
        <div className="voucher-alert voucher-alert-info">
          <i className="fas fa-check-circle"></i> {info}
        </div>
      )}
      {isReversed && (
        <div className="voucher-alert voucher-alert-warn">
          <i className="fas fa-info-circle"></i> This voucher has been reversed. It is locked.
        </div>
      )}

      {/* ── Header card ───────────────────────────────────────────── */}
      <div className="voucher-header-card">
        <div className="field">
          <label>Type</label>
          {isNew ? (
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="JV">JV — Journal Voucher</option>
              <option value="RV">RV — Receipt Voucher</option>
              <option value="PV">PV — Payment Voucher</option>
            </select>
          ) : (
            <input type="text" value={TYPE_LABEL[type] || type} disabled />
          )}
        </div>
        <div className="field">
          <label>Date</label>
          <input
            type="date"
            value={date}
            disabled={readOnly}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="field">
          <label>Doc #</label>
          <input
            type="text"
            value={docNumber}
            disabled={isReversed}
            onChange={(e) => setDocNumber(e.target.value)}
            placeholder="External reference, cheque #, …"
            maxLength={64}
          />
        </div>
        <div className="field field-wide">
          <label>Description</label>
          <input
            type="text"
            value={description}
            disabled={isReversed}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description"
            maxLength={500}
          />
        </div>
        <div className="field">
          <label>Sub-entity</label>
          <select
            value={entityId}
            disabled={readOnly}
            onChange={(e) => setEntityId(e.target.value)}
          >
            <option value="">— Shared / unified —</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>{e.code} · {e.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Lines ─────────────────────────────────────────────────── */}
      <div className="voucher-lines-card">
        <div className="voucher-lines-header">
          <h2>Lines</h2>
          {!readOnly && (
            <button type="button" className="btn-add-line" onClick={addLine}>
              <i className="fas fa-plus"></i> Add line
            </button>
          )}
        </div>

        <div className="lines-table-wrap">
          <table className="lines-table">
            <thead>
              <tr>
                <th className="col-num">#</th>
                <th className="col-account">Account</th>
                <th className="col-currency">Curr.</th>
                <th className="col-rate">Rate</th>
                <th className="col-amount">Debit</th>
                <th className="col-amount">Credit</th>
                <th className="col-amount">Debit (EGP)</th>
                <th className="col-amount">Credit (EGP)</th>
                <th className="col-notes">Notes</th>
                {!readOnly && <th className="col-action"></th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const rate    = toNum(l.exchangeRate) || 1;
                const drLocal = toNum(l.debit)  * rate;
                const crLocal = toNum(l.credit) * rate;
                return (
                  <tr key={idx}>
                    <td className="col-num">{idx + 1}</td>
                    <td className="col-account">
                      {readOnly ? (
                        <div className="account-display">
                          <span className="account-code">{l.accountCode}</span>
                          <span className="account-name">{dn({ name: l.accountName, arabicName: l.accountArabicName }, lang)}</span>
                        </div>
                      ) : (
                        <div className="account-picker">
                          {l.accountId ? (
                            <div className="account-chip">
                              <span className="account-code">{l.accountCode}</span>
                              <span className="account-name">{dn({ name: l.accountName, arabicName: l.accountArabicName }, lang)}</span>
                              <button
                                type="button"
                                className="account-clear"
                                onClick={() => updateLine(idx, { accountId: '', accountCode: '', accountName: '' })}
                                title="Clear"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ) : (
                            <>
                              <input
                                type="text"
                                value={accountQueryByLine[idx] || ''}
                                onChange={(e) => onAccountSearch(idx, e.target.value)}
                                placeholder="Search code or name…"
                              />
                              {(accountResultsByLine[idx] || []).length > 0 && (
                                <ul className="account-results">
                                  {(accountResultsByLine[idx] || []).map((a) => (
                                    <li key={a.id} onClick={() => selectAccount(idx, a)}>
                                      <span className="account-code">{a.code}</span>
                                      <span className="account-name">{dn(a, lang)}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="col-currency">
                      <select
                        value={l.currencyId}
                        disabled={readOnly}
                        onChange={(e) => updateLine(idx, { currencyId: e.target.value })}
                      >
                        <option value="">—</option>
                        {currencies.map((c) => (
                          <option key={c.id} value={c.id}>{c.code}</option>
                        ))}
                      </select>
                    </td>
                    <td className="col-rate">
                      <input
                        type="number"
                        step="0.0001"
                        min="0"
                        value={l.exchangeRate}
                        disabled={readOnly}
                        onChange={(e) => updateLine(idx, { exchangeRate: e.target.value })}
                      />
                    </td>
                    <td className="col-amount">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={l.debit}
                        disabled={readOnly}
                        onChange={(e) => updateLine(idx, { debit: e.target.value, credit: 0 })}
                      />
                    </td>
                    <td className="col-amount">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={l.credit}
                        disabled={readOnly}
                        onChange={(e) => updateLine(idx, { credit: e.target.value, debit: 0 })}
                      />
                    </td>
                    <td className="col-amount cell-local">{fmt(drLocal)}</td>
                    <td className="col-amount cell-local">{fmt(crLocal)}</td>
                    <td className="col-notes">
                      <input
                        type="text"
                        value={l.description}
                        disabled={isReversed}
                        onChange={(e) => updateLine(idx, { description: e.target.value })}
                        placeholder="Line note"
                        maxLength={500}
                      />
                    </td>
                    {!readOnly && (
                      <td className="col-action">
                        <button
                          type="button"
                          className="btn-remove-line"
                          onClick={() => removeLine(idx)}
                          disabled={lines.length <= 1}
                          title={lines.length <= 1 ? 'At least one line required' : 'Remove line'}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6} className="totals-label">Totals (EGP)</td>
                <td className="col-amount totals-amount">{fmt(totalDebit)}</td>
                <td className="col-amount totals-amount">{fmt(totalCredit)}</td>
                <td colSpan={readOnly ? 1 : 2} className={`totals-diff ${balanced ? 'is-balanced' : 'is-unbalanced'}`}>
                  {balanced ? (
                    <><i className="fas fa-check-circle"></i> Balanced</>
                  ) : (
                    <>Δ {fmt(difference)}</>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {!isNew && canCreate && (
        <p className="voucher-form-foot">
          Voucher #{voucher.voucherNumber} · created by {voucher.createdBy?.fullName || voucher.createdBy?.username}
          {voucher.createdAt && <> · {new Date(voucher.createdAt).toLocaleString()}</>}
        </p>
      )}
    </div>
  );
};

export default VoucherFormPage;
